import { Fragment } from 'react';

import { cn } from '@/utils/cn';

/**
 * A display headline whose lines rise into place from behind a mask.
 *
 * The markup is the mask; the motion is not here. Each line sits inside an
 * `overflow: hidden` wrapper and carries the `js-headline-line` hook, and the
 * section that owns the headline drives the rise from its own GSAP timeline --
 * for the hero that is one coordinated entrance with the backdrop and the
 * buttons, played once per page load. An earlier version animated the lines
 * with a CSS keyframe and replayed it from an IntersectionObserver, and the two
 * fought: a CSS animation on an element overrides any transform GSAP writes.
 *
 * Three elements deep, because each level owns exactly one job:
 *
 *   h1 .wonder-headline        the idle float
 *     span .wonder-headline-mask   overflow clip the line emerges from
 *       span .js-headline-line     the entrance (GSAP)
 *         span ...-lift            the hover response
 *
 * `-float`, the entrance and `-lift` all animate `transform`. Flattened onto
 * one element they would overwrite each other and only the last would
 * survive; nested, they multiply and compose.
 *
 * `lines` is a plain array so callers control the break points -- a display
 * headline is typeset, not wrapped, and where each line ends is a design
 * decision rather than a function of the viewport. A line may itself be an
 * array of segments: they share the line, each behind its own inline mask, so
 * the timeline can bring "Internships" up and then "made easy." after it.
 */
export function AnimatedHeadline({ lines, className }) {
  return (
    <h1
      className={cn(
        'wonder-headline text-on-night font-semibold tracking-[-0.03em] uppercase',
        'text-[clamp(1.75rem,3.125vw,3.75rem)] leading-[1.3333]',
        className,
      )}
    >
      {lines.map((line, index) => (
        <Fragment key={String(line)}>
          <span className="wonder-headline-mask">
            {(Array.isArray(line) ? line : [line]).map((segment, at) => (
              <Fragment key={segment}>
                <span className="inline-block overflow-hidden align-bottom">
                  <span className="js-headline-line block will-change-transform">
                    <span
                      className="wonder-headline-lift"
                      style={{ transitionDelay: `${index * 60}ms` }}
                    >
                      {segment}
                    </span>
                  </span>
                </span>
                {/* The segments are inline-block with no whitespace between
                    them, so this is the space between the words. */}
                {at < line.length - 1 ? ' ' : null}
              </Fragment>
            ))}
          </span>
          {/*
            The lines are block elements with no whitespace between them in the
            markup, so the accessible name would come out as one run-together
            string -- "Living.Breathing.Digital Experiences". This space
            separates the words for assistive tech and collapses to nothing
            visually.
          */}
          {index < lines.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </h1>
  );
}
